<template>
    <div>
        <div class="afu-mb-5 afu-mt-2 afu-text-sm afu-text-gray-400">
            <div
                class="afu-text-warning-500"
            >
                <template v-if="codeSendCount > 1">
                    <i class="fas fa-check-circle" />
                    Code re-sent. Also try checking your email spam folder.
                </template>

                <template v-if="user.password_expired">
                    <i class="fas fa-check-circle" />
                    {{ $t('create_password.info') }}
                </template>

                <template v-else>
                    We have sent a reset code to the email above. Please make sure to also check your spam folder.
                </template>
            </div>
        </div>

        <ValidationObserver
            ref="form"
            tag="form"
            class="afu-grid afu-gap-4"
            @submit.prevent="resetPassword()"
        >
            <ValidationProvider
                v-slot="{ errors }"
                name="Reset code"
                rules="required"
            >
                <C1Label label-for="code">
                    <span>Reset code</span>
                    <a
                        href=""
                        :disabled="spinner.forgot_password"
                        class=""
                        @click.stop.prevent="sendForgotPasswordCode()"
                    >
                        <C1Spinner
                            v-if="spinner.forgot_password"
                            variant="primary"
                            small
                        /> (Re-send code)
                    </a>
                </C1Label>

                <C1FormInput
                    id="code"
                    v-model="code"
                    :is-invalid="!!errors[0]"
                    :invalid-feedback="errors[0]"
                    autocomplete="off"
                    name="code"
                    placeholder="Enter code"
                />
            </ValidationProvider>

            <ValidationProvider
                v-slot="{ errors }"
                name="Create a new password"
                rules="required|verify_password"
            >
                <C1Label label-for="password">
                    Create new password
                </C1Label>

                <C1FormInput
                    id="password"
                    v-model="password"
                    :is-invalid="!!errors[0]"
                    :invalid-feedback="errors[0]"
                    name="password"
                    placeholder="Enter new password"
                    type="password"
                />
            </ValidationProvider>
            <C1Alert
                v-if="!!response.message"
                variant="danger"
            >
                <div><strong>{{ response.message_title }}</strong></div>
                <span v-html="response.message" />
            </C1Alert>

            <div>
                <C1Button
                    type="submit"
                    variant="primary"
                    :is-busy="spinner.reset_password"
                    class="afu-mr-4"
                >
                    Sign in and apply
                </C1Button>
                <C1Button
                    type="button"
                    @click.stop.prevent="handleGuestApply()"
                >
                    <span class="afu-text-gray-900 afu-text-primary">{{ $t('label.apply_as_guest') }}</span>
                </C1Button>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
    import has  from 'lodash/has';
    import { mapState, mapActions } from 'vuex';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Button from '@/components/C1Components/C1Button';
    import C1Alert from '@/components/C1Components/C1Alert';
    import C1Label from '@/components/C1Components/C1Label';
    import C1Spinner from '@/components/C1Components/C1Spinner';
    import Cookie from '@/services/cookie';

    export default {
        name: 'AuthResetPassword',

        components: {
            C1Spinner,
            ValidationObserver,
            ValidationProvider,
            C1FormInput,
            C1Button,
            C1Alert,
            C1Label,
        },

        props: {
            user: {
                type: Object,
                default: () => ({}),
            },

            variant: {
                type: String,
                default: 'primary',
            },
        },

        data() {
            return {
                code: '',
                password: '',
                codeSendCount: 1,
                response: {
                    message_title: '',
                    message: '',
                },
                spinner: {
                    forgot_password: false,
                    reset_password: false,
                },
            };
        },

        computed: {
            ...mapState({
                $_apiMessage: state => state.apiMessage,
            }),
        },

        methods: {
            ...mapActions('me', {
                $_storeAllUserData: 'storeAllUserData',
            }),

            ...mapActions('auth', {
                $_forgotPassword: 'forgotPassword',
                $_authLogin: 'login',
                $_resetPassword: 'resetPassword',
            }),

            async resetPassword() {
                const validate = await this.$refs.form.validate();
                if (!validate) {
                    return false;
                }

                this.resetResponse();

                const data = {
                    email: this.user.email,
                    password: this.password,
                    code: this.code,
                };

                this.spinner.reset_password = true;
                this.$_resetPassword(data).then(() => {
                    this.login();
                    this.spinner.reset_password = false;
                }).catch((error) => {
                    const errorCode = has(error, 'response.data.error') ? error.response.data.error : 'ServerErrorException';
                    this.response.message_title = 'Reset code failed';
                    this.response.message = this.$_apiMessage[errorCode];
                    this.spinner.reset_password = false;
                });
            },

            login() {
                const data = {
                    email: this.user.email,
                    password: this.password,
                };

                this.$_authLogin(data).then((response) => {
                    this.$_storeUserAfterLogin(response);
                    this.$emit('afterLogin');
                });
            },

            $_storeUserAfterLogin(data) {
                this.$_storeAllUserData(data);
                Cookie.setToken(data.access_token);
            },

            sendForgotPasswordCode() {
                const data = {
                    email: this.user.email,
                    code: true,
                };

                this.resetResponse();

                this.spinner.forgot_password = true;
                this.$_forgotPassword(data).then(() => {
                    this.codeSendCount += 1;
                }).catch((error) => {
                    const errorCode = has(error, 'response.data.error') ? error.response.data.error : 'ServerErrorException';
                    this.response.message_title = 'Forgot password failed';
                    this.response.message = this.$_apiMessage[errorCode];
                }).finally(() => {
                    this.spinner.forgot_password = false;
                });
            },

            resetResponse() {
                this.response.message_title = '';
                this.response.message = '';
            },

            handleGuestApply() {
                this.$emit('handleGuestApply');
            },
        },
    };
</script>
